import React, { useEffect, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import { Input, Button, Space, Form, notification, Modal } from "antd";
import axios from "axios";
import { useAuth } from "../../../context/AuthContext";
import moment from "moment";
import { useCallback, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import debounce from "lodash/debounce";
export const statusMapping: Record<string, string> = { "1": "Draft", "2": "Approved", "3": "Waiting for Approved", "4": "Complete", "5": "Canceled" };
export const useAdjustmentTable = () => {
    const [form] = Form.useForm();
    const [warehouses, setWarehouses] = useState([]);
    const [brands, setBrands] = useState([]);
    const [isWhDisabled, setIsWhDisabled] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [tableData, setTableData] = useState<any[]>([]);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [autosaveStatus, setAutosaveStatus] = useState<string>('stopped');
    const { userData } = useAuth();
    const [isConfirmModalVisible, setIsConfirmModalVisible] = useState(false);
    const [remark, setRemark] = useState("Adjustment");
    const location = useLocation();

    useEffect(() => {
        form.setFieldsValue({ status: "Draft" });
        form.setFieldsValue({ adjustment_date: moment().format('DD/MM/YYYY') });
        const fetchData = async () => {
            try {
                const [whRes, brRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/warehouses`),
                    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/brands`)
                ]);
                setWarehouses(whRes.data.map((w: any) => ({ label: w.warehouse_name, value: w.warehouse_code })));
                setBrands(brRes.data.map((b: any) => ({ label: b.description, value: b.code })));
            } catch (e) { console.error(e); }
        };
        fetchData();
    }, [form]);
    useEffect(() => {
        const state = location.state as { adjustment_no: string };
        if (state?.adjustment_no) {
            const loadData = async () => {
                try {
                    const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/get/${state.adjustment_no}`);
                    const { header, details } = res.data;
                    form.setFieldsValue({ ...header, status: statusMapping[header.status] });
                    setTableData(details.map((d: any) => ({ ...d, key: d.item_code })));
                    setIsWhDisabled(true);
                } catch (e) { console.error(e); }
            };
            loadData();
        }
    }, [location.state, form]);

    const handleWhChange = async (value: string) => {
        console.log("Warehouse Selected Value:", value);
        try {
            const currentWarehouse = form.getFieldValue('warehouse');
            if (!currentWarehouse) return;
            const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/generate-no`);
            if (response.data && response.data.adjustment_no) {
                form.setFieldsValue({ adjustment_no: response.data.adjustment_no });
                setIsWhDisabled(true);
                setAutosaveStatus('processing');
                const reverseStatusMap: Record<string, string> = { "Draft": "1", "Approved": "2", "Waiting for Approved": "3", "Complete": "4", "Canceled": "5" };
                const formData = {
                    status: String(reverseStatusMap[form.getFieldValue('status')] || "1"),
                    adjustment_no: String(response.data.adjustment_no || ""),
                    adjustment_date: String(form.getFieldValue('adjustment_date') || ""),
                    brand: String(form.getFieldValue('brand') || ""),
                    event_no: String(form.getFieldValue('event_no') || ""),
                    warehouse: String(value || "")
                };

                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/save`, formData);
                setTimeout(() => setAutosaveStatus('idle'), 300);
            }
        } catch (e) {
            console.error("Gagal mendapatkan nomor adjustment:", e);
            setAutosaveStatus('error');
        }
    };
    const getColumnSearchProps = (dataIndex: string) => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: any) => (
            <div style={{ padding: 8 }}>
                <Input
                    placeholder={`Search ${dataIndex}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => confirm()}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button type="primary" onClick={() => confirm()} icon={<SearchOutlined />} size="small" style={{ width: 90 }}>Search</Button>
                    <Button onClick={() => clearFilters()} size="small" style={{ width: 90 }}>Reset</Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
        onFilter: (value: any, record: any) => record[dataIndex] ? record[dataIndex].toString().toLowerCase().includes(value.toLowerCase()) : '',
    });

    const debouncedSaveHeader = useCallback(
        debounce(async (allValues: any) => {
            const adjNo = form.getFieldValue("adjustment_no");
            const currentStatus = form.getFieldValue("status");
            const reverseStatusMap: Record<string, string> = { "Draft": "1", "Approved": "2", "Waiting for Approved": "3", "Complete": "4", "Canceled": "5" };
            if (!adjNo || reverseStatusMap[currentStatus] !== "1") return;
            setAutosaveStatus("processing");
            try {
                const reverseStatusMap: Record<string, string> = { "Draft": "1", "Approved": "2", "Waiting for Approved": "3", "Complete": "4", "Canceled": "5" };
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/save`, {
                    ...allValues,
                    status: reverseStatusMap[allValues.status] || "1",
                    adjustment_no: adjNo
                });
                setAutosaveStatus("idle");
            } catch (e) { setAutosaveStatus("error"); }
        }, 1000), [form]
    );

    const debouncedSaveDetail = useMemo(
        () => debounce(async (record: any) => {
            const currentStatus = form.getFieldValue("status");
            const reverseStatusMap: Record<string, string> = { "Draft": "1", "Approved": "2", "Waiting for Approved": "3", "Complete": "4", "Canceled": "5" };
            if (reverseStatusMap[currentStatus] !== "1") return;
            setAutosaveStatus("processing");
            try {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/save-detail`, {
                    adjustment_no: form.getFieldValue('adjustment_no'),
                    ...record
                });
                setAutosaveStatus("idle");
            } catch (e) { setAutosaveStatus("error"); }
        }, 500),
        [form]
    );

    const columns = useMemo(() => [
        { title: 'Item Code', dataIndex: 'item_code', key: 'item_code', sorter: (a: any, b: any) => a.item_code.localeCompare(b.item_code), ...getColumnSearchProps('item_code') },
        { title: 'Item Name', dataIndex: 'item_name', key: 'item_name', sorter: (a: any, b: any) => a.item_name.localeCompare(b.item_name), ...getColumnSearchProps('item_name') },
        { title: 'Location', dataIndex: 'location', key: 'location', sorter: (a: any, b: any) => a.location.localeCompare(b.location), ...getColumnSearchProps('location') },
        { title: 'UoM', dataIndex: 'uom', key: 'uom', sorter: (a: any, b: any) => a.uom.localeCompare(b.uom), ...getColumnSearchProps('uom') },
        { title: 'Qty', dataIndex: 'qty', key: 'qty', render: (_: any, record: any) => <Input type="number" value={record.qty} onChange={(e) => updateLocalData(record.key, 'qty', e.target.value)} /> },
        { title: 'Reason', dataIndex: 'reason', key: 'reason', render: (_: any, record: any) => <Input value={record.reason} onChange={(e) => updateLocalData(record.key, 'reason', e.target.value)} /> },
    ], [tableData]);

    const updateLocalData = (key: string, field: string, value: any) => {
        setTableData(prev => prev.map(item => {
            if (item.key === key) {
                const currentQty = field === 'qty' ? (parseFloat(value) || 0) : (parseFloat(item.qty) || 0);
                const calculatedAmount = (parseFloat(item.hpp) || 0) * currentQty;
                const newItem = { ...item, [field]: value, amount: calculatedAmount };
                debouncedSaveDetail(newItem);
                return newItem;
            }
            return item;
        }));
    };

    const handleDelete = async () => {
        const adjNo = form.getFieldValue('adjustment_no');
        const itemsToDelete = tableData.filter(item => selectedRowKeys.includes(item.key)).map(i => i.item_code);
        if (adjNo && itemsToDelete.length > 0) {
            try {
                await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/delete-detail`, { data: { adjustment_no: adjNo, item_codes: itemsToDelete } });
            } catch (e) { console.error("Gagal hapus data DB:", e); }
        }
        setTableData(prev => prev.filter(item => !selectedRowKeys.includes(item.key)));
        setSelectedRowKeys([]);
    };

    const rowSelection = { selectedRowKeys, onChange: (keys: React.Key[]) => setSelectedRowKeys(keys) };



    const handleSelectItem = async (item: any) => {
        const isDuplicate = tableData.some((existingItem) => existingItem.item_code === item.item_code);
        if (isDuplicate) {
            notification.warning({
                message: "Peringatan",
                description: "item sudah ada di tabel tidak bisa duplikat !"
            });
            return;
        }
        const initialAmount = (parseFloat(item.hpp) || 0) * (parseFloat(item.qty) || 0);
        const newData = { ...item, key: Date.now().toString(), qty: item.qty || '', reason: '', uom: item.uom || '', location: item.location || '', amount: initialAmount };
        setTableData(prev => [...prev, newData]);
        setIsModalVisible(false);
        const adjNo = form.getFieldValue('adjustment_no');
        const currentStatus = form.getFieldValue("status");
        const reverseStatusMap: Record<string, string> = { "Draft": "1", "Approved": "2", "Waiting for Approved": "3", "Complete": "4", "Canceled": "5" };
        if (adjNo && reverseStatusMap[currentStatus] === "1") {
            setAutosaveStatus('processing');
            try {
                await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/save-detail`, {
                    adjustment_no: String(adjNo),
                    warehouse: String(form.getFieldValue('warehouse') || ""),
                    item_code: String(item.item_code || ""),
                    item_name: String(item.item_name || ""),
                    location: String(item.location || ""),
                    uom: String(item.uom || ""),
                    qty: String(item.qty || "0"),
                    amount: String(initialAmount || "0"),
                    reason: String(item.reason || "")
                });
                setTimeout(() => setAutosaveStatus('idle'), 300);
            } catch (e) { setAutosaveStatus('error'); }
        }
    };

    const handleNew = () => {
        form.resetFields();
        form.setFieldsValue({ status: "Draft", adjustment_date: moment().format('DD/MM/YYYY') });
        setTableData([]);
        setIsWhDisabled(false);
        setSelectedRowKeys([]);
        notification.success({ message: "Reset form Success" });
    };

    const handleVoid = () => {
        const adjNo = form.getFieldValue("adjustment_no");
        if (!adjNo) return;
        Modal.confirm({
            title: "Confirm Void",
            content: "Are you sure you want to void your data? This process permanently deletes data.",
            onOk: async () => {
                try {
                    await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/void/${adjNo}`);
                    handleNew();
                    notification.success({ message: "Delete Data Success" });
                } catch (e: any) {
                    const errCode = e.response?.status || "500";
                    notification.error({ message: `Failed Void, Check Error [${errCode}]` });
                }
            },
        });
    };

    const handleRemarkChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        if (!value.startsWith("Adjustment")) {
            setRemark("Adjustment");
        } else {
            setRemark(value);
        }
    };

    const handleSubmit = () => {
        if (!form.getFieldValue('adjustment_no')) return;
        setIsConfirmModalVisible(true);
    };

    const handleConfirmSubmit = async () => {
        try {
            const adjNo = form.getFieldValue('adjustment_no');
            await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/adjustment/submit`, {
                adjustment_no: adjNo,
                warehouse: form.getFieldValue('warehouse'),
                amount_total: tableData.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0),
                remark: remark,
                request_by: userData?.kode_grup1
            });
            setIsConfirmModalVisible(false);
            handleNew();
            notification.success({ message: "Submitted to Approval Successfully" });
        } catch (e) {
            notification.error({ message: "Failed to submit approval" });
        }
    };

    return {
        tableData,
        columns,
        rowSelection,
        form,
        warehouses,
        brands,
        handleWhChange,
        isWhDisabled,
        isModalVisible,
        setIsModalVisible,
        handleSelectItem,
        handleDelete,
        autosaveStatus,
        debouncedSaveHeader,
        handleNew,
        handleVoid,
        selectedRowKeys,
        handleSubmit,
        isConfirmModalVisible,
        setIsConfirmModalVisible,
        remark,
        handleRemarkChange,
        handleConfirmSubmit
    };
};